// Vocabulário visual das telas da esteira. Portado do protótipo — ADR-0005.
// A unidade é a linha, não o card: borda, fundo e sombra são orçamento de
// hierarquia, e gastar em tudo não deixa sobrar para o que importa.

import type { CSSProperties, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { esteira, fila, type TomDeEstado } from '@anery/design-system';

import { cn } from './cn';
import type {
  AneryChip,
  AneryEtapa,
  AneryKpi,
  AneryTag,
  AneryTone,
  EstadoDaTrilha,
  TravaDoCaso,
  Trilha,
} from './tiposDaEsteira';

// ─────────────────────────────────────────────────────────────────────────────
// Tom → cor da marca
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Três estados operacionais, três cores do cliente — e cada uma com papel fixo
 * no produto inteiro, não escolhida por tela:
 *   teal · corre sozinho      roxo · espera pessoa      rosa · barrado
 * `info` é navy (estrutura) e `neutral` é cinza (sem carga).
 */
const TOM: Record<AneryTone, string> = {
  ok: 'curso',
  warn: 'espera',
  block: 'barrado',
  info: 'info',
  neutral: 'neutral',
};

const TEXTO: Record<AneryTone, string> = {
  ok: 'text-[var(--teal)]',
  warn: 'text-[var(--roxo)]',
  block: 'text-[var(--rosa)]',
  info: 'text-[var(--navy)]',
  neutral: 'text-[var(--tinta-3)]',
};

// ─────────────────────────────────────────────────────────────────────────────
// Contexto da tela
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Título e subtítulo são da CASCA — repeti-los aqui punha a mesma frase duas
 * vezes na mesma dobra. O que sobra para a tela é o que só ela sabe: os números
 * do recorte que ela mostra.
 */
export function Contexto({ chips }: { chips?: AneryChip[] }) {
  if (!chips || chips.length === 0) return null;
  return (
    <p className="anery-contexto">
      {chips.map((c, i) => (
        <span key={i}>
          {c.strong && <b className="num">{c.strong}</b>}
          {c.strong && ' '}
          {c.label}
        </span>
      ))}
    </p>
  );
}

/** O fio navy que separa assunto. É a única peça estrutural do produto. */
export function SecaoTitulo({ children, extra }: { children: ReactNode; extra?: ReactNode }) {
  return (
    <h2 className="anery-faixa cond mt-10">
      {children}
      {extra && (
        <span className="ml-auto text-[0.75rem] font-normal normal-case tracking-normal text-[var(--tinta-2)]">
          {extra}
        </span>
      )}
    </h2>
  );
}

export function Painel({
  titulo,
  sub,
  extra,
  children,
  className,
}: {
  titulo: string;
  sub?: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('anery-secao', className)}>
      <h3 className="anery-faixa cond">
        {titulo}
        {extra && (
          <span className="ml-auto text-[0.75rem] font-normal normal-case tracking-normal text-[var(--tinta-2)]">
            {extra}
          </span>
        )}
      </h3>
      {sub && <p className="anery-secao-sub">{sub}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabela
// ─────────────────────────────────────────────────────────────────────────────

export interface Coluna {
  rot: string;
  /** Coluna numérica: alinha à direita, mono, tabular. */
  num?: boolean;
}

/**
 * `<table>` cru de propósito — ver o comentário de `.anery-tabela` no CSS: o
 * componente de tabela da plataforma traz altura, padding e hover próprios, e
 * cobrir aquilo daqui é briga de especificidade que se perde calada numa troca
 * de versão.
 */
export function Tabela({ colunas, children }: { colunas: Coluna[]; children: ReactNode }) {
  return (
    <div className="anery-tabela">
      <table>
        <thead>
          <tr>
            {colunas.map((c) => (
              <th key={c.rot} data-num={c.num ? '' : undefined} scope="col">
                {c.rot}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Td({
  num,
  fraco,
  className,
  children,
}: {
  num?: boolean;
  /** Coluna de apoio — categoria, origem, observação. */
  fraco?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <td
      data-num={num ? '' : undefined}
      className={cn(fraco && 'text-[var(--tinta-2)]', className)}
    >
      {children}
    </td>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Estado
// ─────────────────────────────────────────────────────────────────────────────

/** Estado escrito, na cor do estado. Cor sozinha nunca carrega a informação. */
export function Tag({ label, tone }: AneryTag) {
  return (
    <span className="anery-estado cond" data-tom={TOM[tone]}>
      {label}
    </span>
  );
}

/**
 * Etiqueta de operadora.
 *
 * Cor fixa por operadora, não por estado: quem opera reconhece a carteira pela
 * cor antes de ler o nome, e a fila é de todas mesmo com o piloto sendo o
 * Bradesco. Operadora que não estiver no mapa cai no cinza, em vez de sumir.
 */
// Operadora é configuração, não código: nada de mapa nome→cor chumbado.
// O tom sai de um índice estável sobre a paleta já medida.
// ponytail: hash ingênuo; quando a tabela de operadora existir, a cor vem dela.
// Sem os tons de SLA: verde/âmbar/vermelho já significam prazo, e o nome da
// operadora não pode tomar emprestado esse significado.
const PALETA_DE_OPERADORA = [esteira.barrado, fila.dlq, fila.processando, fila.ociosa] as const;

function tomDaOperadora(nome: string): TomDeEstado {
  let soma = 0;
  for (const ch of nome) soma = (soma + ch.charCodeAt(0)) % 997;
  return PALETA_DE_OPERADORA[soma % PALETA_DE_OPERADORA.length] ?? fila.ociosa;
}

export function Operadora({ nome }: { nome: string }) {
  const tom = tomDaOperadora(nome);
  return (
    <span
      className="cond"
      style={{
        color: tom.tinta,
        background: tom.fundo,
        border: `1px solid ${tom.borda}`,
        borderRadius: '999px',
        padding: '0.1rem 0.5rem',
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}
    >
      {nome}
    </span>
  );
}

export function Flag({ label, tone }: AneryTag) {
  return (
    <span className={cn('cond text-[0.6875rem] uppercase tracking-[0.08em]', TEXTO[tone])}>
      {label}
    </span>
  );
}

/** Marca de que a saída passa por pessoa. O protótipo repete isso de propósito. */
export function Humano({ children = 'humano' }: { children?: ReactNode }) {
  return <span className="cond text-[0.6875rem] text-[var(--roxo)]">✓ {children}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Quadro
// ─────────────────────────────────────────────────────────────────────────────

export function Board({ children }: { children: ReactNode }) {
  // auto-fit em vez de contagem fixa: a captação passou a ter uma 5ª coluna
  // (Declinado) e o board precisa caber 3, 4 ou 5 sem quebrar linha.
  return (
    <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-2 xl:[grid-template-columns:repeat(auto-fit,minmax(14rem,1fr))]">
      {children}
    </div>
  );
}

export function BoardColuna({
  nome,
  contagem,
  children,
}: {
  nome: string;
  contagem: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0">
      <h3 className="anery-coluna-tit cond">
        <span className="min-w-0 flex-1">{nome}</span>
        <span className="num font-normal tracking-normal text-[var(--tinta-3)]">{contagem}</span>
      </h3>
      {children}
    </section>
  );
}

const COR_TOM: Record<AneryTone, string> = {
  ok: 'var(--teal)',
  warn: 'var(--roxo)',
  block: 'var(--rosa)',
  info: 'var(--navy)',
  neutral: 'var(--tinta-3)',
};

export function WorkCard({
  titulo,
  sub,
  meta,
  tone = 'neutral',
  flag,
  onClick,
  destacado,
  chegou,
  pendente,
  piscando,
  substatus,
  children,
}: {
  titulo: string;
  sub?: string;
  meta?: string;
  tone?: AneryTone;
  flag?: AneryTag;
  onClick?: () => void;
  /** Realce momentâneo — usado quando o item acaba de mudar de coluna. */
  destacado?: boolean;
  /** Acabou de chegar nesta coluna: anima a entrada, uma vez. */
  chegou?: boolean;
  /**
   * O caso está esperando uma PESSOA. Pinta o card inteiro, porque é a
   * distinção que quem varre a fila precisa fazer sem ler: o que está tingido
   * depende de alguém; o que está limpo a máquina resolve.
   */
  pendente?: AneryTone;
  /** Onde o caso está dentro da fase — a linha que o IW não sabe preencher. */
  substatus?: string;
  /** Escalou e ninguém respondeu: o card pulsa até alguém pegar. */
  piscando?: boolean;
  children?: ReactNode;
}) {
  const conteudo = (
    <>
      {flag && (
        <span className="anery-ficha-est">
          <Flag {...flag} />
        </span>
      )}
      <span className="anery-ficha-tit">{titulo}</span>
      {substatus && (
        <span className="anery-ficha-sub cond" style={{ color: 'var(--navy)' }}>
          {substatus}
        </span>
      )}
      {sub && <span className="anery-ficha-sub">{sub}</span>}
      {meta && <span className="anery-ficha-meta">{meta}</span>}
      {children}
      {!flag && tone !== 'neutral' && (
        <span className="anery-ficha-meta">
          <Tag label={TOM[tone]} tone={tone} />
        </span>
      )}
    </>
  );

  if (!onClick) {
    return (
      <div
        className="anery-ficha"
        data-destacado={destacado ? '' : undefined}
        data-chegou={chegou ? '' : undefined}
        data-pendente={pendente ? '' : undefined}
        data-piscando={piscando ? '' : undefined}
        style={pendente ? ({ '--tom-pendente': COR_TOM[pendente] } as CSSProperties) : undefined}
      >
        {conteudo}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="anery-ficha"
      data-destacado={destacado ? '' : undefined}
      data-chegou={chegou ? '' : undefined}
      data-pendente={pendente ? '' : undefined}
      data-piscando={piscando ? '' : undefined}
      style={pendente ? ({ '--tom-pendente': COR_TOM[pendente] } as CSSProperties) : undefined}
    >
      {conteudo}
    </button>
  );
}

/**
 * As quatro frentes de um caso, uma linha cada.
 *
 * O traço é CONTAGEM: tantos segmentos quantos passos a trilha tem de verdade
 * — 7 no kit Bradesco, 2 no da AMIL. Barra sem denominador é a peça mais cara
 * de manter, e aqui o denominador existe.
 *
 * Existe porque uma posição só por caso escondia a frente mais atrasada. A
 * linha `trava` responde "onde travou" sem ninguém abrir o caso.
 */
/**
 * Semáforo da frente, em QUATRO estados — e a cor é o último deles a chegar.
 *
 * O ponto também muda de forma: cheio quando fechou, anel quando anda, cheio
 * e pulsando quando estourou, tracejado quando espera outra frente. Quem não
 * distingue verde de vermelho lê a forma; quem lê rápido lê a cor; e o
 * `aria-label` da barra carrega a palavra. Nenhuma informação mora só no
 * matiz — é a mesma regra do resto do produto.
 */
function pontoDa(e: EstadoDaTrilha): 'fechada' | 'espera' | 'anda' | 'estourou' {
  if (e.feitos >= e.total) return 'fechada';
  if (e.esperando) return 'espera';
  return e.tom === 'block' ? 'estourou' : 'anda';
}

/** O semáforo, sozinho. Card e diálogo usam ESTE — não duas cópias. */
export function PontoDaTrilha({ estado }: { estado: EstadoDaTrilha }) {
  return <i className="anery-ponto" data-estado={pontoDa(estado)} aria-hidden="true" />;
}

/**
 * A barra, sozinha. O traço é contagem: um segmento por passo que a frente
 * tem de verdade, e o rótulo acessível diz os dois números e a situação.
 */
/**
 * Um traço por passo: os feitos em verde, o da vez em amarelo (em andamento), o resto vazio. A barra conta o
 * ANDAMENTO; a saúde do prazo (estourou, espera) é do ponto ao lado do nome, para as duas não dizerem coisas
 * diferentes com a mesma cor. Trilha de itens sem ordem (o cadastro) não tem passo da vez: só os feitos.
 */
export function BarraDaTrilha({ estado, nome }: { estado: EstadoDaTrilha; nome: string }) {
  const fechada = estado.feitos >= estado.total;
  const deItens = estado.unidade === 'item';
  // O mesmo texto vai ao leitor de tela e à dica do mouse: a barra se explica para quem não lê a cor.
  const descricao = fechada
    ? `${nome}: ${estado.total} de ${estado.total} · concluída`
    : `${nome}: ${deItens ? `${estado.feitos} de ${estado.total}` : `passo ${estado.feitos + 1} de ${estado.total}`}${estado.situacao ? ` · ${estado.situacao}` : ''}`;
  return (
    <span className="anery-trilha-traco" role="img" aria-label={descricao} title={descricao}>
      {Array.from({ length: estado.total }, (_, i) => (
        <span
          key={i}
          data-feito={i < estado.feitos ? '' : undefined}
          data-da-vez={i === estado.feitos && !fechada && !deItens ? '' : undefined}
        />
      ))}
    </span>
  );
}

export function Trilhas({
  trilhas,
  trava,
  ordem,
  rotulos,
  rotulosLongos,
}: {
  /**
   * PARCIAL de propósito: quem só mede uma frente passa só ela, e as outras
   * não são desenhadas. Barra vazia afirmaria "zero passos feitos", e "não
   * medimos" é diferente — num quadro de cobrança, a diferença custa caro.
   */
  trilhas: Partial<Record<Trilha, EstadoDaTrilha>>;
  trava?: TravaDoCaso;
  ordem: Trilha[];
  /** Token → "A · avaliação" para a tira. O token é do modelo; o rótulo é de gente. */
  rotulos: Partial<Record<Trilha, string>>;
  /** Token → "A · Avaliação" por extenso, para a linha de trava. */
  rotulosLongos: Partial<Record<Trilha, string>>;
}) {
  return (
    <span className="anery-trilhas">
      {ordem.map((t) => {
        const e = trilhas[t];
        if (e === undefined) return null;
        const fechada = e.feitos >= e.total;
        return (
          <span key={t} className="anery-trilha" data-esperando={e.esperando ? '' : undefined}>
            <PontoDaTrilha estado={e} />
            <span className="cond anery-trilha-rot">{rotulos[t] ?? t}</span>
            <BarraDaTrilha estado={e} nome={rotulos[t] ?? t} />
            <span className="num anery-trilha-idade">{fechada ? '✓' : (e.idade ?? '')}</span>
          </span>
        );
      })}

      {/* Derivada de `trilhas`, nunca digitada: a frente que pode andar e está
          parada há mais tempo, com o papel de quem precisa agir. */}
      {trava && (
        <span className="anery-trava">
          <span className="cond anery-trava-rot">trava · {rotulosLongos[trava.trilha] ?? trava.trilha}</span>
          <span className="anery-trava-mot">{trava.motivo}</span>
          <span className="anery-trava-dono">{trava.responsavel}</span>
        </span>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Números
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Eram quatro caixas grandes no topo da tela. Um número grande só se justifica
 * quando ele É o assunto da página — e em toda tela do ciclo o assunto é a
 * fila, não a média do mês. Viram linhas: valor em navy na frente, rótulo
 * depois, origem no fim.
 */
export function KpiGrid({ kpis }: { kpis: AneryKpi[] }) {
  return (
    <div>
      {kpis.map((k) => (
        <div key={k.label} className="anery-item">
          <span
            className={cn(
              'num w-[7.5rem] shrink-0 whitespace-nowrap text-[0.9375rem] font-medium',
              k.tone ? TEXTO[k.tone] : 'text-[var(--navy)]',
            )}
          >
            {/* `null` renderiza "—": o protótipo não tinha o número e o mock
                não inventa um plausível. */}
            {k.value ?? '—'}
          </span>
          <span className="min-w-0 flex-1 text-[0.875rem]">
            {k.label}
            {k.delta && <span className="ml-2 text-[0.75rem] text-[var(--teal)]">{k.delta}</span>}
            {k.detail && (
              <span className="mt-0.5 block text-[0.75rem] leading-snug text-[var(--tinta-2)]">
                {k.detail}
              </span>
            )}
          </span>
          {k.source && (
            <span className="cond hidden shrink-0 text-[0.6875rem] uppercase tracking-[0.08em] text-[var(--tinta-3)] sm:block">
              {k.source}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** O ciclo em sequência — que é o que ele é. Ver a nota do arquivo sobre a barra. */
export function Ciclo({ etapas }: { etapas: AneryEtapa[] }) {
  return (
    <div className="anery-funil">
      {etapas.map((e, i) => (
        <span key={e.nome} className="anery-funil-etapa">
          {i > 0 && (
            <span aria-hidden="true" className="anery-funil-seta">
              ›
            </span>
          )}
          <span className="text-[0.8125rem] text-[var(--tinta-2)]">{e.nome}</span>
          <span className="num text-[0.9375rem] font-medium text-[var(--navy)]">{e.valor}</span>
          {e.unidade && <span className="text-[0.75rem] text-[var(--tinta-3)]">{e.unidade}</span>}
        </span>
      ))}
    </div>
  );
}

/** Medida contínua. A única barra que sobrou — ver `.anery-medida` no CSS. */
export function Medida({ pct, rotulo }: { pct: number; rotulo: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between text-[0.75rem]">
        <span className="text-[var(--tinta-2)]">{rotulo}</span>
        <span className="num">{pct > 0 ? `${pct}%` : '—'}</span>
      </div>
      <div className="anery-medida mt-1.5" role="img" aria-label={`${rotulo}: ${pct}%`}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Texto de apoio
// ─────────────────────────────────────────────────────────────────────────────

export function Nota({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <p className="mt-8 max-w-[68ch] text-[0.8125rem] leading-relaxed text-[var(--tinta-2)]">
      <b className="font-semibold text-[var(--navy)]">{titulo}</b> {children}
    </p>
  );
}

export function Dica({ children }: { children: ReactNode }) {
  return (
    <p className="mt-8 max-w-[68ch] text-[0.8125rem] leading-relaxed text-[var(--tinta-2)]">
      {children}
    </p>
  );
}

/** Interrupção — o único lugar que ganha tarja. */
export function Aviso({ children, tone = 'block' }: { children: ReactNode; tone?: AneryTone }) {
  return (
    <p className="anery-aviso" data-tom={tone === 'ok' ? 'ok' : undefined}>
      {children}
    </p>
  );
}

/** Documento de terceiro: citação, não card. */
export function DocView({ children }: { children: ReactNode }) {
  return <div className="anery-doc">{children}</div>;
}

export function Destaque({ children }: { children: ReactNode }) {
  // rgba literal, e não `bg-[var(--teal)]/15`: o modificador de opacidade do
  // Tailwind não atravessa `var()` — sai sem fundo nenhum, em silêncio.
  return <span className="bg-[rgba(0,181,173,0.16)] px-1 text-[var(--tinta)]">{children}</span>;
}

/**
 * Linha de checklist com quatro estados: conferido, faltando, **dúvida** (a IA
 * não resolveu) e **conferindo**. O terceiro é o que dá a sensação de trabalho
 * acontecendo — sem ele o item pula de "!" para "✓" e parece que nada foi
 * verificado.
 */
export function ChkRow({
  texto,
  ok,
  validando,
  duvida,
}: {
  texto: ReactNode;
  ok: boolean;
  validando?: boolean;
  duvida?: boolean;
}) {
  const estado = validando ? 'lendo' : ok ? 'ok' : duvida ? 'duvida' : 'falta';
  return (
    <div className="anery-chk" data-estado={estado}>
      <span className="anery-chk-marca" aria-hidden="true">
        {validando ? (
          <Loader2 className="mx-auto h-3 w-3 animate-spin" />
        ) : ok ? (
          '✓'
        ) : duvida ? (
          '?'
        ) : (
          '!'
        )}
      </span>
      <span className="anery-chk-txt">
        {texto}
        {validando && (
          <span className="cond ml-1.5 text-[0.6875rem] uppercase tracking-[0.08em] text-[var(--navy)]">
            validando…
          </span>
        )}
      </span>
    </div>
  );
}

